import { StyleSheet, View, TouchableOpacity } from 'react-native';
import { Navigation, Plus, Minus, Layers } from 'lucide-react-native';
import type { LucideIcon } from 'lucide-react-native';
import { useTheme, ELEVATION, SPACING, BORDER_RADIUS } from '../../theme';
import { useWebHover } from '../../hooks/useWebHover';
import { CURSOR_POINTER, TRANSITION_DOCK_INSET } from '../../utils/webStyles';
import { useUIStore } from '../../store/uiStore';
import { useDesktopShell } from '../../hooks/useDesktopShell';
import { Tooltip } from '../common/Tooltip';

const CONTROL_BUTTON_SIZE = 40;
// Phone/tablet only from here down (see MAP_CONTROLS_CLEARANCE's comment
// below) — above the tab bar's floating attribution line, which CSS lifts
// to margin-bottom: 76px at that width (leafletHelpers.ts).
const CONTROLS_BOTTOM = 110;
// Desktop keeps its own corner: Leaflet's default attribution bar sits in
// it uncollapsed (no tab bar to lift it), so the stack clears that instead
// of the phone's 76px tab-bar line (Sept 29 2026, M1).
const ATTRIBUTION_HEIGHT_DESKTOP = 17;
const CONTROLS_BOTTOM_DESKTOP = ATTRIBUTION_HEIGHT_DESKTOP + SPACING.md;
// Every floating map control sits SPACING.md off the edge it's anchored to.
const CONTROLS_RIGHT = SPACING.md;
// Zoom in, zoom out, locate, layers — the phone/tablet count (every basemap
// and overlay choice moved into the layers sheet, so this stays 4 however
// many overlays ship). Sept 2026 A2: the desktop shell drops the layers
// button (it moved to the map-tools rail), leaving 3 there — but
// MAP_CONTROLS_CLEARANCE below is read only by MapSignInOverlay's phone
// corner chip, so this constant stays the phone number on purpose.
const CONTROL_COUNT = 4;

// The stack's own chrome on top of the buttons: 1px border top and bottom,
// plus a hairline divider between each pair. Small, but leaving it out ate
// most of the gap below.
const CONTROL_STACK_CHROME = 2 + (CONTROL_COUNT - 1);

/**
 * Bottom offset a phone overlay needs to clear to sit above the control
 * stack (MapSignInOverlay stacks its help/avatar chips there). Derived
 * rather than hand-tuned: the previous literal went stale the moment a
 * fourth button was added, and the two overlays overlapped.
 */
export const MAP_CONTROLS_CLEARANCE =
  CONTROLS_BOTTOM + CONTROL_COUNT * CONTROL_BUTTON_SIZE + CONTROL_STACK_CHROME + SPACING.sm;

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onLocateMe?: () => void;
  onOpenLayers?: () => void;
  /**
   * True when anything in the layers sheet is off its default: satellite
   * basemap, or either overlay drawn. Tints the button so the stack says
   * "something is on" without trying to say which.
   */
  layersActive?: boolean;
  /**
   * True while the desktop-shell Layers panel is docked open (uiStore.
   * layersPanelOpen). Drives aria-expanded — unlike layersActive, this says
   * whether the panel is open right now, not whether anything in it is set
   * to a non-default value. False (the default) on the phone sheet, which
   * is a dialog rather than a disclosure.
   */
  layersExpanded?: boolean;
  /**
   * Extra right offset to clear a right-docked panel on the desktop shell
   * (useRightDockInset in uiStore) — 0 on phones and tablets, where nothing
   * docks. Without this the stack painted at its usual `right: 12` on top
   * of the defense plan panel and the structure detail panel alike.
   */
  rightInset?: number;
}

function ControlButton({
  icon: Icon,
  label,
  onPress,
  testID,
  tint,
  pressed,
  hasPopup,
  expanded,
}: {
  icon: LucideIcon;
  label: string;
  onPress: () => void;
  testID?: string;
  /** Overrides the default ink, to mark a toggle as active. */
  tint?: string;
  /** Set on a toggle button so its on/off state reaches assistive tech. */
  pressed?: boolean;
  /** Set on a button that opens a dialog rather than doing something. */
  hasPopup?: boolean;
  /** Set on a button that discloses a panel — aria-expanded, not
   *  aria-pressed (a panel is open/closed, not on/off). */
  expanded?: boolean;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <TouchableOpacity
      {...hoverProps}
      style={[
        styles.controlButton,
        hovered && { backgroundColor: colors.hoverSurface },
        CURSOR_POINTER,
      ]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={pressed === undefined ? undefined : { selected: pressed }}
      // RN-web drops accessibilityState on export — see Table.tsx's sort
      // header for the same fix. Carry the toggle state as a raw aria-*
      // prop so axe (and screen readers) actually see it.
      aria-pressed={pressed}
      aria-haspopup={hasPopup ? 'dialog' : undefined}
      aria-expanded={expanded}
      testID={testID}>
      <Icon size={20} color={tint ?? colors.text} />
    </TouchableOpacity>
  );
}

export function MapControls({
  onZoomIn,
  onZoomOut,
  onLocateMe,
  onOpenLayers,
  layersActive = false,
  layersExpanded = false,
  rightInset = 0,
}: MapControlsProps) {
  const { colors } = useTheme();
  // Same gate MapLegend and MapSignInOverlay use: every sheet holds the tab
  // bar down while it is open, and the user is not reading the map then.
  // It lives here rather than at the call sites because it used to: the
  // native map wrapped this in a HideWhenSheetOpen and the web map did not,
  // so on phones the stack painted on top of the structure sheet at its
  // zIndex of 1100. A gate one platform can forget is a gate that breaks.
  // The subscription sits in this leaf on purpose — that was the reason for
  // the old wrapper, keeping it out of Map so a tab-bar flip never
  // re-renders the marker children.
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const isDesktopShell = useDesktopShell();
  if (!tabBarVisible) return null;
  return (
    <View
      testID="map-controls-stack"
      style={[
        styles.controlStack,
        TRANSITION_DOCK_INSET,
        {
          bottom: isDesktopShell ? CONTROLS_BOTTOM_DESKTOP : CONTROLS_BOTTOM,
          right: CONTROLS_RIGHT + rightInset,
          backgroundColor: colors.surfaceElevated,
          borderColor: colors.border,
        },
      ]}>
      <Tooltip label="Zoom in">
        <ControlButton icon={Plus} label="Zoom in" onPress={onZoomIn} />
      </Tooltip>
      <View style={[styles.controlDivider, { backgroundColor: colors.border }]} />
      <Tooltip label="Zoom out">
        <ControlButton icon={Minus} label="Zoom out" onPress={onZoomOut} />
      </Tooltip>
      {onLocateMe && (
        <>
          <View style={[styles.controlDivider, { backgroundColor: colors.border }]} />
          <Tooltip label="Center on my location">
            <ControlButton
              icon={Navigation}
              label="Center map on my location"
              onPress={onLocateMe}
            />
          </Tooltip>
        </>
      )}
      {onOpenLayers && (
        <>
          <View style={[styles.controlDivider, { backgroundColor: colors.border }]} />
          {/* Opens the layers sheet rather than flipping anything. This used
              to be two buttons that each toggled something without naming
              it or showing which way it was set. */}
          <Tooltip label="Map layers">
            <ControlButton
              icon={Layers}
              label="Map layers"
              onPress={onOpenLayers}
              testID="map-layers-button"
              tint={layersActive ? colors.primary : undefined}
              hasPopup
              expanded={layersExpanded}
            />
          </Tooltip>
        </>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  // Single strip of map controls (zoom in, zoom out, locate, layers)
  // stacked vertically. Sits above the floating nav bar and Leaflet's pane
  // stack (~600) so it's never hidden.
  controlStack: {
    position: 'absolute',
    // `bottom` and `right` are set inline (platform-dependent) — see the render.
    borderRadius: BORDER_RADIUS.md,
    borderWidth: 1,
    overflow: 'hidden',
    ...ELEVATION.floating,
    zIndex: 1100,
  },
  controlButton: {
    width: CONTROL_BUTTON_SIZE,
    height: CONTROL_BUTTON_SIZE,
    alignItems: 'center',
    justifyContent: 'center',
  },
  controlDivider: {
    height: StyleSheet.hairlineWidth,
    width: '100%',
  },
});
